import type { ModuleId, ModuleConfig } from '../types/module'

export const MODULES: Record<ModuleId, ModuleConfig> = {
  'dt-os': {
    id: 'dt-os',
    label: 'DT-OS',
    name: 'Operations',
    tagline: 'End-to-end operations management',
    description:
      'Complete operational visibility and control for your Shopify store. Manage orders, inventory, fulfillment, and team workflows from a single command center.',
    color: '#2C6ECB',
    route: '/dt-os',
    reportsRoute: '/reports/dt-os',
    hasReports: true,
    tier: 'starter',
    features: [
      'Order lifecycle management',
      'Return & exchange workflows',
      'FIFO inventory allocation',
      'Bosta shipping integration',
      'Team task management',
      'Custom order tagging',
    ],
  },
  'dt-fo': {
    id: 'dt-fo',
    label: 'DT-FO',
    name: 'Forecasting',
    tagline: 'Executive intelligence for revenue targets',
    description:
      'Monthly targets, current position dashboard, and a decision engine that tells you exactly what is needed to break even, hit your profit target, or reach your monthly goal.',
    color: '#00A47C',
    route: '/dt-fo',
    reportsRoute: '/reports/dt-fo',
    hasReports: true,
    tier: 'growth',
    features: [
      'Monthly revenue & order targets',
      'Current position tracking',
      'Break-even calculator',
      'Profit target engine',
      'Daily performance projections',
      'SKU & category breakdown',
    ],
  },
  'dt-mo': {
    id: 'dt-mo',
    label: 'DT-MO',
    name: 'Marketing',
    tagline: 'Data-driven marketing attribution',
    description:
      'Connect marketing spend to actual revenue. Track campaigns, measure ROI, understand customer acquisition costs, and optimise your marketing mix.',
    color: '#8C5CE6',
    route: '/dt-mo',
    reportsRoute: '/reports/dt-mo',
    hasReports: true,
    tier: 'growth',
    features: [
      'UTM campaign tracking',
      'Social vs web attribution',
      'Customer acquisition cost',
      'Marketing ROI dashboard',
      'Channel performance reports',
      'Influencer tracking',
    ],
  },
  'dt-cs': {
    id: 'dt-cs',
    label: 'DT-CS',
    name: 'Customer',
    tagline: 'Customer intelligence at scale',
    description:
      'Deep customer analytics including LTV, segmentation, cohort analysis, and churn prediction. Turn customer data into retention strategies.',
    color: '#E67E22',
    route: '/dt-cs',
    reportsRoute: '/reports/dt-cs',
    redirectTo: '/reports/dt-cs',
    hasReports: true,
    tier: 'scale',
    features: [
      'Customer lifetime value',
      'RFM segmentation',
      'Cohort retention analysis',
      'Churn prediction',
      'VIP customer tracking',
      'Customer journey mapping',
    ],
  },
  'dt-fc': {
    id: 'dt-fc',
    label: 'DT-FC',
    name: 'Financial',
    tagline: 'Real profitability from settlement data',
    description:
      'True P&L using COD settlement data, COGS from product records, and advertising costs. Track gross profit, net profit, carrier fees, return costs, and cash status.',
    color: '#E74C3C',
    route: '/dt-fc',
    reportsRoute: '/reports/dt-fc',
    hasReports: true,
    tier: 'scale',
    features: [
      'Real P&L from settlement data',
      'COGS tracking with fallback',
      'Marketing cost management',
      'Carrier fees & return costs',
      'Cash collection status',
      'Financial health validators',
    ],
  },
  'dt-st': {
    id:          'dt-st',
    label:       'DT-ST',
    name:        'Sales Team',
    tagline:     'Upsell queue management for your sales team',
    description: 'Manage upsell and re-engagement queues for your sales agents. Distribute orders, track sessions, record outcomes, and create upsell orders directly from the queue.',
    color:       '#0D7B6B',
    route:       '/dt-st',
    reportsRoute: '/reports/dt-st',
    hasReports:  false,
    tier:        'growth',
    features: [
      'Before-shipping upsell queue',
      'Round-robin agent distribution',
      'Session lifecycle tracking',
      'Ready & upsell outcome recording',
      'Shopify order creation from queue',
      'Timeline audit trail',
    ],
  },
  'dt-cf': {
    id:           'dt-cf',
    label:        'DT-CF',
    name:         'Confirmation',
    tagline:      'Order confirmation workflow for your team',
    description:  'Manage the confirmation queue for COD orders. Distribute orders to agents, track confirmation sessions, record outcomes, and escalate unresolvable orders.',
    color:        '#2C6ECB',
    route:        '/dt-cf',
    reportsRoute: '/reports/dt-cf',
    hasReports:   false,
    tier:         'growth',
    features: [
      'Confirmation queue management',
      'Round-robin agent distribution',
      'Session lifecycle tracking',
      'Postpone & no-answer handling',
      'Escalation to CS workflow',
      'Timeline audit trail',
    ],
  },
  'dt-sc': {
    id:           'dt-sc',
    label:        'DT-SC',
    name:         'Shipping Confirmation',
    tagline:      'Pre-dispatch shipping confirmation workflow',
    description:  'Confirm orders before handing to the courier. Verify address, timing, and availability. Handle customer change requests, delays, and cancellations before dispatch.',
    color:        '#8C5CE6',
    route:        '/dt-sc',
    reportsRoute: '/reports/dt-sc',
    hasReports:   false,
    tier:         'growth',
    features: [
      'Pre-dispatch confirmation queue',
      'Round-robin agent distribution',
      'Customer change & delay handling',
      'Transfer rules to other workflows',
      'Escalation via Reason Builder',
      'Timeline audit trail',
    ],
  },
  'dt-an': {
    id:          'dt-an',
    label:       'DT-AN',
    name:        'Analytics',
    tagline:     'KPI engine, performance tracking, and executive dashboards',
    description: 'The Analytics Platform is the single source of truth for KPIs across every DT Labs module. Track performance, analyze trends, monitor conversions, and deliver role-based executive dashboards.',
    color:       '#0066CC',
    route:       '/dt-an',
    reportsRoute: '/reports/dt-an',
    hasReports:  false,
    tier:        'scale',
    features: [
      'Configurable KPI engine with merchant overrides',
      'Performance evaluation (Green / Yellow / Orange / Red)',
      'Role-based executive dashboards (CEO, COO, Manager)',
      'Full-funnel conversion analytics',
      'Timing analytics across every workflow',
      'Trend analysis with daily time series',
    ],
  },
  'dt-exec': {
    id:          'dt-exec',
    label:       'DT-EXEC',
    name:        'Executive Intelligence',
    tagline:     'Role-based executive dashboards, alerts, insights, and opportunities',
    description: 'The Executive Intelligence Platform delivers a complete decision-making layer for every executive role — from CEO to department managers. Powered by real-time data from all DT Labs modules.',
    color:       '#7C3AED',
    route:       '/dt-exec',
    reportsRoute: '/reports',
    hasReports:  false,
    tier:        'scale',
    features: [
      'Role-based dashboards for 10 executive roles',
      'Real-time alerts with root cause and suggested actions',
      'Smart deterministic insights and opportunity detection',
      'Period-over-period comparison and trend analysis',
      'KPI scorecards with achievement tracking',
      'Heatmaps, rankings, and company timeline',
    ],
  },
  'dt-dt': {
    id:          'dt-dt',
    label:       'DT-DT',
    name:        'Delivery Tracking',
    tagline:     'Post-dispatch delivery monitoring and follow-up',
    description: 'Track shipments after handoff to couriers. Monitor delivery statuses, manage failed attempts, follow up with customers, and reduce return rates.',
    color:       '#D97706',
    route:       '/workspace/delivery-tracking',
    reportsRoute: '/reports',
    hasReports:  false,
    tier:        'growth',
    features: [
      'Post-dispatch shipment tracking',
      'Failed delivery follow-up queue',
      'Delivery status monitoring',
      'Return rate reduction workflows',
      'Agent queue distribution',
      'Activity timeline',
    ],
  },
}

export const MODULE_LIST = Object.values(MODULES)

export const NAV_ORDER: ModuleId[] = ['dt-os', 'dt-fo', 'dt-mo', 'dt-cs', 'dt-fc', 'dt-st', 'dt-cf', 'dt-sc', 'dt-dt', 'dt-an', 'dt-exec']
